import React from "react";
import { Chip } from "components/common/Chip";
import { Tooltip } from "@/components/common/Tooltip";

/**
 * The fixed ladder of shortcut chips, ordered ascending by exponent.
 * Negative scales (µ, m, %) sit left of `(none)`; positive scales (K, M, B)
 * sit right. Any integer exponent not on this ladder is entered through
 * the always-visible exponent input — no "Custom…" chip.
 */
export const SCALE_LADDER: { exponent: number; label: string }[] = [
  { exponent: -6, label: "µ" },
  { exponent: -3, label: "m" },
  { exponent: -2, label: "%" },
  { exponent: 0, label: "(none)" },
  { exponent: 3, label: "K" },
  { exponent: 6, label: "M" },
  { exponent: 9, label: "B" },
];

interface ScaleChipRowProps {
  /** Current exponent. Lights the matching chip if one exists in the ladder. */
  active: number;
  /** When the active scale was auto-carried over from a previous unit
   *  (same-qkind unit change). Renders a subtle "(kept)" suffix on the
   *  matching chip. */
  isCarried: boolean;
  onPick: (exponent: number) => void;
}

/**
 * Scale shortcut chips. Clicking one calls `onPick(exponent)` which the
 * parent writes into the staged exponent state. The matching chip lights
 * up when the active exponent equals one of the ladder values; otherwise
 * no chip is lit (the input is showing a "custom" value like `10^5`).
 */
export function ScaleChipRow({ active, isCarried, onPick }: ScaleChipRowProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Scale"
      style={{ display: "flex", gap: "var(--spacing-xs)", flexWrap: "wrap" }}
    >
      {SCALE_LADDER.map(({ exponent, label }) => {
        const isActive = exponent === active;
        const showCarried = isCarried && isActive && exponent !== 0;
        const chipSpan = (
          <span key={exponent} role="radio" aria-checked={isActive}>
            <Chip
              label={showCarried ? `${label} (kept)` : label}
              active={isActive}
              variant={isActive ? "info" : "default"}
              onClick={() => onPick(exponent)}
            />
          </span>
        );
        return showCarried ? (
          <Tooltip
            key={exponent}
            title="Scale carried from previous unit — click any other chip to clear"
          >
            {chipSpan}
          </Tooltip>
        ) : (
          chipSpan
        );
      })}
    </div>
  );
}
